// Renewal Board REST client — FE-SCRM-008.
//
// Wraps the BE-SCRM-007 endpoints. Filter params are encoded as
// CSV-separated multi-values (the BE parses ``g.get("producer")``
// via ``_multi_csv``); booleans round-trip as ``true`` strings.

import { api } from "@/api/client";

import type {
  BoardFilters,
  PresetListResponse,
  RenewalBoardCellResponse,
  RenewalBoardOpportunity,
  RenewalBoardPreset,
  RenewalBoardResearchQueueResponse,
  RenewalBoardResponse,
  RenewalBoardTimelineResponse,
} from "./types";


// ── Filter encoding ──────────────────────────────────────────────


function encodeFilters(
  filters: BoardFilters,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (filters.producer.length) out.producer = filters.producer.join(",");
  if (filters.broker_location.length) {
    out.broker_location = filters.broker_location.join(",");
  }
  if (filters.lives_band.length) {
    out.lives_band = filters.lives_band.join(",");
  }
  if (filters.industry.length) out.industry = filters.industry.join(",");
  if (filters.tags.length) out.tags = filters.tags.join(",");
  if (filters.opportunity_type.length) {
    out.opportunity_type = filters.opportunity_type.join(",");
  }
  if (filters.renewal_confidence.length) {
    out.renewal_confidence = filters.renewal_confidence.join(",");
  }
  if (filters.cadence_health.length) {
    out.cadence_health = filters.cadence_health.join(",");
  }
  if (filters.has_active_alerts === true) {
    out.has_active_alerts = "true";
  }
  return out;
}


function qs(params: Record<string, string>): string {
  const u = new URLSearchParams(params);
  const s = u.toString();
  return s ? `?${s}` : "";
}


// ── Endpoints ────────────────────────────────────────────────────


export interface BoardWindow {
  start_month?: string; // YYYY-MM; defaults to current month server-side
  num_months?: number;  // defaults to 12 server-side
}


export function fetchRenewalBoard(
  window: BoardWindow,
  filters: BoardFilters,
  opts?: { signal?: AbortSignal },
): Promise<RenewalBoardResponse> {
  const params: Record<string, string> = encodeFilters(filters);
  if (window.start_month) params.start_month = window.start_month;
  if (window.num_months !== undefined) {
    params.num_months = String(window.num_months);
  }
  return api.get<RenewalBoardResponse>(
    `/api/v1/sales/renewal-board${qs(params)}`,
    opts,
  );
}


export interface CellQuery {
  month: string;
  stage: string;
  filters: BoardFilters;
  min_lives?: number;
  sort?:
    | "cadence_health_severity"
    | "days_until_renewal"
    | "value_estimate"
    | "prospect_name";
}


export function fetchRenewalBoardCell(
  q: CellQuery,
  opts?: { signal?: AbortSignal },
): Promise<RenewalBoardCellResponse> {
  const params: Record<string, string> = encodeFilters(q.filters);
  params.month = q.month;
  params.stage = q.stage;
  if (q.min_lives !== undefined) params.min_lives = String(q.min_lives);
  if (q.sort) params.sort = q.sort;
  return api.get<RenewalBoardCellResponse>(
    `/api/v1/sales/renewal-board/cell${qs(params)}`,
    opts,
  );
}


export function fetchRenewalBoardTimeline(
  filters: BoardFilters,
  opts?: { signal?: AbortSignal },
): Promise<RenewalBoardTimelineResponse> {
  return api.get<RenewalBoardTimelineResponse>(
    `/api/v1/sales/renewal-board/timeline${qs(encodeFilters(filters))}`,
    opts,
  );
}


export function fetchResearchQueue(
  filters: BoardFilters,
  opts?: { signal?: AbortSignal },
): Promise<RenewalBoardResearchQueueResponse> {
  return api.get<RenewalBoardResearchQueueResponse>(
    `/api/v1/sales/renewal-board/research-queue${qs(encodeFilters(filters))}`,
    opts,
  );
}


// ── Presets ──────────────────────────────────────────────────────


export function fetchPresets(
  opts?: { signal?: AbortSignal },
): Promise<PresetListResponse> {
  return api.get<PresetListResponse>(
    "/api/v1/sales/renewal-board/presets",
    opts,
  );
}


export function createPreset(input: {
  name: string;
  filter_json: Record<string, unknown>;
}): Promise<RenewalBoardPreset> {
  return api.post<RenewalBoardPreset>(
    "/api/v1/sales/renewal-board/presets",
    input,
  );
}


export function patchPreset(
  preset_id: string,
  input: Partial<{
    name: string;
    filter_json: Record<string, unknown>;
    display_order: number;
    is_hidden: boolean;
  }>,
): Promise<RenewalBoardPreset> {
  return api.patch<RenewalBoardPreset>(
    `/api/v1/sales/renewal-board/presets/${preset_id}`,
    input,
  );
}


export function deletePreset(preset_id: string): Promise<unknown> {
  return api.delete(
    `/api/v1/sales/renewal-board/presets/${preset_id}`,
  );
}


export function pinPresetAsDefault(
  preset_id: string,
): Promise<RenewalBoardPreset> {
  return api.post<RenewalBoardPreset>(
    `/api/v1/sales/renewal-board/presets/${preset_id}/pin-as-default`,
    {},
  );
}


export function hidePreset(
  preset_id: string,
): Promise<RenewalBoardPreset> {
  return api.post<RenewalBoardPreset>(
    `/api/v1/sales/renewal-board/presets/${preset_id}/hide`,
    {},
  );
}


export function unhidePreset(
  preset_id: string,
): Promise<RenewalBoardPreset> {
  return api.post<RenewalBoardPreset>(
    `/api/v1/sales/renewal-board/presets/${preset_id}/unhide`,
    {},
  );
}


// ── Opportunity stage move (BE-SCRM-004) ─────────────────────────


export interface MoveStageInput {
  to_stage: string;
  confirm_reopen?: boolean;
  // Required by BE-SCRM-026 when ``to_stage === "closed_lost"``.
  lost_reason?: string | null;
  lost_to_broker?: string | null;
  lost_notes?: string | null;
}


export function moveOpportunityStage(
  opportunity_id: string,
  input: MoveStageInput,
): Promise<RenewalBoardOpportunity> {
  return api.post<RenewalBoardOpportunity>(
    `/api/v1/sales/opportunities/${opportunity_id}/move-stage`,
    input,
  );
}


export function patchOpportunityRenewal(
  opportunity_id: string,
  input: { renewal_month: number; renewal_year: number },
): Promise<RenewalBoardOpportunity> {
  return api.patch<RenewalBoardOpportunity>(
    `/api/v1/sales/opportunities/${opportunity_id}`,
    input,
  );
}
